// store/roomStore.ts
import { create } from 'zustand';
import { roomData } from '../data';
import type { Room } from '../types';

interface RoomStore {
  rooms: Room[];
  loading: boolean;
  adults: string;
  kids: string;
  total: number;
  setAdults: (adults: string) => void;
  setKids: (kids: string) => void;
  handleCheck: (e: React.FormEvent) => void;
  resetRoomFilterData: () => void;
  fetchRooms: () => Promise<void>;
  fetchRoomImages: (roomId: number) => Promise<string[]>;
}

// API Service
const apiService = {
  async getRooms(): Promise<Room[]> {
    try {
      // Simulate API call - replace with actual endpoint
      const response = await fetch('/api/rooms');
      if (!response.ok) throw new Error('Failed to fetch rooms');
      return await response.json();
    } catch (error) {
      console.error('Error fetching rooms:', error);
      // Fallback to local data if API fails
      return roomData;
    }
  },

  async getRoomImages(roomId: number): Promise<string[]> {
    try {
      const response = await fetch(`/api/rooms/${roomId}/images`);
      if (!response.ok) throw new Error('Failed to fetch room images');
      const data = await response.json();
      return data.images || [];
    } catch (error) {
      console.error('Error fetching room images:', error);
      return [];
    }
  },

  async filterRooms(criteria: { adults: number; kids: number }): Promise<Room[]> {
    try {
      const response = await fetch('/api/rooms/filter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(criteria),
      });
      if (!response.ok) throw new Error('Failed to filter rooms');
      return await response.json();
    } catch (error) {
      console.error('Error filtering rooms:', error);
      // Fallback to local filtering
      return roomData.filter((room) => 
        criteria.adults + criteria.kids <= room.maxPerson
      );
    }
  }
};

export const useRoomStore = create<RoomStore>((set, get) => ({
  rooms: [],
  loading: false,
  adults: '1 Adult',
  kids: '0 Kid',
  total: 1, // Initialize total

  setAdults: (adults) => {
    const kids = get().kids;
    const total = parseInt(adults[0]) + parseInt(kids[0]);
    set({ adults, total });
  },
  
  setKids: (kids) => {
    const adults = get().adults;
    const total = parseInt(adults[0]) + parseInt(kids[0]);
    set({ kids, total });
  },

  // Fetch rooms from API
  fetchRooms: async () => {
    set({ loading: true });
    try {
      const rooms = await apiService.getRooms();
      set({ rooms, loading: false });
    } catch (error) {
      console.error('Failed to fetch rooms:', error);
      set({ loading: false });
    }
  },

  // Fetch room images from API
  fetchRoomImages: async (roomId: number) => {
    try {
      return await apiService.getRoomImages(roomId);
    } catch (error) {
      console.error('Failed to fetch room images:', error);
      return [];
    }
  },

  // Handle room filtering with API
  handleCheck: async (e: React.FormEvent) => {
    e.preventDefault();
    const { adults, kids } = get();
    const total = parseInt(adults[0]) + parseInt(kids[0]);
    
    set({ loading: true });
    
    try {
      // Use API for filtering
      const filteredRooms = await apiService.filterRooms({
        adults: parseInt(adults[0]),
        kids: parseInt(kids[0]),
      });
      
      setTimeout(() => {
        set({
          loading: false,
          rooms: filteredRooms.length > 0 ? filteredRooms : [],
        });
      }, 500);
    } catch (error) {
      // Fallback to local filtering
      const filterRooms = roomData.filter((room) => total <= room.maxPerson);
      setTimeout(() => {
        set({
          loading: false,
          rooms: filterRooms.length > 0 ? filterRooms : roomData,
        });
      }, 500);
    }
  },

  resetRoomFilterData: async () => {
    set({ loading: true });
    try {
      const rooms = await apiService.getRooms();
      set({
        adults: '1 Adult',
        kids: '0 Kid',
        total: 1,
        rooms,
        loading: false,
      });
    } catch (error) {
      set({
        adults: '1 Adult',
        kids: '0 Kid',
        total: 1,
        rooms: roomData,
        loading: false,
      });
    }
  },
}));